import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Container } from '../ui/Container';
import { Check, Zap, X, Building2, Landmark } from 'lucide-react';
import { useState, useMemo } from 'react';
import React from 'react';
import { LoginPromptModal } from '../ui/LoginPromptModal';
import { useUser } from '../../hooks/useUser';
import PaymentModal, { DEFAULT_PAYMENT_TYPE, DEFAULT_COUNTRY_CODE } from '../ui/PaymentModal';
import MobileMoneyStatusDisplay from '../ui/MobileMoneyStatusDisplay';
import { PricingPlan, PaymentDetails } from '../../types/profile';
import { useModernSubscription } from '../../hooks/useModernSubscription';
import { useSubscription } from '../../hooks/useSubscription';
import { useMobileMoneyPayment } from '../../hooks/useMobileMoneyPayment';
import { SubscriptionPlan, CustomerType, getRelevantFeatures, formatTokenAmount as formatTokens } from '../../types/subscription';

// Configuration basée sur le backend
// Les CustomerType sont maintenant importés de types/subscription

// Plans statiques de fallback (utilisés si le backend ne répond pas)
// ✨ Mis à jour avec la structure du backend (Juin 2025)
const FALLBACK_SME_PLANS = [
	{
		id: 'sme-freemium',
		name: 'PME Freemium',
		description: 'ERP gratuit pour découvrir nos fonctionnalités essentielles',
		price: '0',
		customerType: CustomerType.SME,
		features: [
			'✓ 900 Écritures comptables/mois',
			'✓ 250 Factures & reçus/mois',
			'✓ 200 Produits en stock',
			'✓ 300 Messages assistant IA Adha/mois',
			'✓ 2 Utilisateurs',
			'✓ 2 Go de stockage cloud',
			'✗ Scoring de crédit',
			'✗ Accès financement'
		],
		tokenAmount: '300',
		highlighted: false
	},
	{
		id: 'sme-basic',
		name: 'Basic',
		description: 'Plan idéal pour les petites entreprises en croissance',
		price: '9.9',
		customerType: CustomerType.SME,
		features: [
			'✓ 1 800 Écritures comptables/mois',
			'✓ 600 Factures & reçus/mois',
			'✓ 500 Produits en stock',
			'✓ 1 000 Messages assistant IA Adha/mois',
			'✓ 3 Utilisateurs',
			'✓ Scoring de crédit avancé',
			'✓ Financement jusqu\'à 500 USD',
			'✓ 10 Go de stockage cloud'
		],
		tokenAmount: '1000',
		highlighted: false
	},
	{
		id: 'sme-premium',
		name: 'Premium',
		description: 'Plan complet pour PME avec toutes les fonctionnalités',
		price: '19.9',
		customerType: CustomerType.SME,
		features: [
			'✓ Écritures comptables illimitées',
			'✓ Facturation illimitée',
			'✓ 1 000 Produits en stock',
			'✓ Assistant IA Adha illimité',
			'✓ 5 Utilisateurs',
			'✓ Scoring crédit + Accès experts Wanzo',
			'✓ Financement jusqu\'à 2 500 USD',
			'✓ 100 Go de stockage cloud'
		],
		tokenAmount: 'Illimité',
		highlighted: true
	}
];

const FALLBACK_FI_PLANS = [
	{
		id: 'institution-freemium',
		name: 'Découverte',
		description: 'Découvrez notre plateforme institution gratuitement',
		price: '0',
		customerType: CustomerType.FINANCIAL_INSTITUTION,
		features: [
			'✓ 50 Entreprises suivies en portefeuille',
			'✓ 2 Portefeuilles de suivi',
			'✓ 1 Agence connectée',
			'✓ 30 Messages assistant IA Adha/mois',
			'✓ 10 Évaluations de risque PME/mois',
			'✓ 1 Go de stockage cloud',
			'✗ Traitement des demandes de crédit',
			'✗ Analytics IA avancés'
		],
		tokenAmount: '30',
		highlighted: false
	},
	{
		id: 'institution-basic',
		name: 'Basic Institution',
		description: 'Pour les institutions financières en démarrage',
		price: '99',
		customerType: CustomerType.FINANCIAL_INSTITUTION,
		features: [
			'✓ 500 Entreprises suivies en portefeuille',
			'✓ 10 Portefeuilles de suivi',
			'✓ 5 Agences connectées',
			'✓ 500 Messages assistant IA Adha/mois',
			'✓ 100 Évaluations de risque PME/mois',
			'✓ 50 Demandes de crédit traitées/mois',
			'✓ 3 Utilisateurs',
			'✓ 10 Go de stockage cloud'
		],
		tokenAmount: '500',
		highlighted: false
	},
	{
		id: 'institution-premium',
		name: 'Premium Institution',
		description: 'Solution complète pour institutions financières',
		price: '299',
		customerType: CustomerType.FINANCIAL_INSTITUTION,
		features: [
			'✓ Entreprises en portefeuille illimitées',
			'✓ Portefeuilles de suivi illimités',
			'✓ Réseau d\'agences illimité',
			'✓ Assistant IA Adha illimité',
			'✓ Évaluations de risque PME illimitées',
			'✓ Traitement des demandes de crédit illimité',
			'✓ 20 Utilisateurs + Analytics IA avancés',
			'✓ 50 Go de stockage cloud'
		],
		tokenAmount: 'Illimité',
		highlighted: true
	}
];

const CUSTOM_SOLUTIONS = [
	{
		name: 'ERP Sur-Mesure PME',
		description: 'Solution ERP complètement personnalisée pour votre entreprise',
		price: 'Sur devis',
		customerType: CustomerType.SME,
		isCustom: true,
		features: [
			'ERP adapté à vos processus métier',
			'Modules sectoriels spécialisés',
			'Déploiement privé ou cloud',
			'IA locale pour confidentialité',
			'Interface personnalisée',
			'Formation équipes incluse',
			'Utilisateurs illimités',
			'Support prioritaire SLA',
			'Consultant dédié permanent'
		],
		ctaText: 'Prendre rendez-vous',
		category: 'PME'
	},
	{
		name: 'Plateforme Financière Sur-Mesure',
		description: 'Infrastructure financière complète adaptée à votre institution',
		price: 'Sur devis',
		customerType: CustomerType.FINANCIAL_INSTITUTION,
		isCustom: true,
		features: [
			'Plateforme gestion risques propriétaire',
			'Modèles scoring personnalisés',
			'Lac de données sécurisé',
			'Centrale de risque privée',
			'Simulation produits sur-mesure',
			'Intégration Core Banking complète',
			'Modules réglementaires (OHADA, CEMAC)',
			'Interface multi-agences',
			'Formation certifiante équipes'
		],
		ctaText: 'Planifier une démonstration',
		category: 'IF'
	}
];

// Export fonction pour obtenir tous les plans (pour compatibilité avec les services)
export const getAllPlans = () => {
	return [...FALLBACK_SME_PLANS, ...FALLBACK_FI_PLANS, ...CUSTOM_SOLUTIONS];
};

/**
 * Convertit un SubscriptionPlan backend vers le format UI local
 */
function convertBackendPlanToUI(plan: SubscriptionPlan): any {
	// Extraire les fonctionnalités pertinentes (max 6 pour l'affichage)
	const relevantFeatures = getRelevantFeatures(plan.features, 6);
	
	// Convertir en format texte pour l'affichage
	const featureStrings = relevantFeatures.map(f => {
		const prefix = f.enabled ? '✓' : '✗';
		return `${prefix} ${f.description || f.label}`;
	});

	return {
		id: plan.id,
		name: plan.name,
		description: plan.description,
		price: plan.monthlyPriceUSD === 0 ? '0' : plan.monthlyPriceUSD.toString(),
		annualPrice: plan.annualPriceUSD,
		customerType: plan.customerType,
		features: featureStrings,
		tokenAmount: formatTokens(plan.tokenAllocation.monthlyTokens),
		highlighted: plan.isPopular,
		tags: plan.tags,
		// Garder la référence au plan backend pour l'expand
		_backendPlan: plan
	};
}

export function Pricing() {
	const navigate = useNavigate();
	const { user } = useUser();
	
	// Hook pour charger les plans depuis le backend
	const { plans: backendPlans, isLoading: plansLoading } = useModernSubscription();
	
	// Hook pour gérer les abonnements et paiements
	const { changePlan, isLoading: paymentLoading } = useSubscription();
	
	// Hook pour le polling Mobile Money
	const { 
		state: mobileMoneyState, 
		startPayment: startMobileMoneyPolling, 
		cancelPolling: cancelMobileMoneyPolling,
		resetState: resetMobileMoneyState
	} = useMobileMoneyPayment();
	
	const [isAnnual, setIsAnnual] = useState(false);
	const [selectedCustomerType, setSelectedCustomerType] = useState<CustomerType>(CustomerType.SME);
	/**
	 * Mode "coopérative" : on est sur le segment SME mais on affiche la carte
	 * tarifaire dédiée (5 USD × N coopérants/mois) au lieu de la grille standard.
	 * Backend : la company sera créée avec variant='cooperative'.
	 */
	const [isCooperativeView, setIsCooperativeView] = useState(false);
	/** Nombre de coopérants déclarés par l'utilisateur. Min 1, Max 500. */
	const [cooperativeMembers, setCooperativeMembers] = useState(20);
	const [loginModalOpen, setLoginModalOpen] = useState(false);
	const [selectedPlan, setSelectedPlan] = useState<any>(null);
	
	// États pour le modal de paiement
	const [showPaymentModal, setShowPaymentModal] = useState(false);
	const [selectedPlanForPayment, setSelectedPlanForPayment] = useState<PricingPlan | null>(null);
	const [paymentType, setPaymentType] = useState<'card' | 'mobile' | 'manual'>(DEFAULT_PAYMENT_TYPE);
	const [paymentInfo, setPaymentInfo] = useState<PaymentDetails>({
		paymentMethod: DEFAULT_PAYMENT_TYPE,
		amount: '',
		cardNumber: '',
		cvv: '',
		expiryDate: '',
		mobileNumber: '',
		mobileMoneyProvider: '',
		countryCode: DEFAULT_COUNTRY_CODE,
		mobilePin: '',
	});
	const [formError, setFormError] = useState('');
	const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
	const [promoCode, setPromoCode] = useState('');
	const [promoDiscount, setPromoDiscount] = useState(0);

	// Convertir les plans backend vers le format UI ou utiliser les fallbacks
	const displayPlans = useMemo(() => {
		if (backendPlans.length > 0) {
			// Convertir les plans backend
			const converted = backendPlans.map(convertBackendPlanToUI);
			return {
				sme: converted.filter(p => p.customerType === CustomerType.SME),
				fi: converted.filter(p => p.customerType === CustomerType.FINANCIAL_INSTITUTION)
			};
		}
		// Fallback vers les plans statiques
		return {
			sme: FALLBACK_SME_PLANS,
			fi: FALLBACK_FI_PLANS
		};
	}, [backendPlans]);

	// Détermination du plan actuel
	let currentPlan = 'Free';
	if (user && (user as any).plan) {
		currentPlan = (user as any).plan;
	} else if (user && (user as any)['https://ksuit.app/plan']) {
		currentPlan = (user as any)['https://ksuit.app/plan'];
	}

	// Filtrer les plans selon le type de client sélectionné
	const getDisplayPlans = () => {
		const regularPlans = selectedCustomerType === CustomerType.SME ? displayPlans.sme : displayPlans.fi;
		const customSolutions = CUSTOM_SOLUTIONS.filter(solution => solution.customerType === selectedCustomerType);
		return [...regularPlans, ...customSolutions];
	};

	// Fonction de validation des codes promo
	const validatePromoCode = (code: string): number => {
		const promoCodes = {
			'WELCOME10': 0.10,
			'SAVE20': 0.20,
			'NEWUSER': 0.15,
			'ANNUAL25': 0.25
		};
		return promoCodes[code as keyof typeof promoCodes] || 0;
	};

	// Fonction de soumission du paiement
	const handlePaymentSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setFormError('');

		if (!selectedPlanForPayment) {
			setFormError('Aucun plan sélectionné');
			return;
		}

		// Validation selon la méthode de paiement
		if (paymentType === 'card') {
			if (!paymentInfo.cardNumber || !paymentInfo.cvv || !paymentInfo.expiryDate) {
				setFormError('Veuillez remplir tous les champs de la carte.');
				return;
			}
		} else if (paymentType === 'mobile') {
			if (!paymentInfo.mobileMoneyProvider || !paymentInfo.mobileNumber || !paymentInfo.mobilePin) {
				setFormError('Veuillez remplir tous les champs Mobile Money.');
				return;
			}
		}

		try {
			console.log('📤 Traitement du paiement:', {
				planId: selectedPlanForPayment.id,
				planName: selectedPlanForPayment.name,
				paymentType,
				paymentInfo,
				billingPeriod
			});

			// Envoyer la requête au backend - utiliser l'UUID du plan (id) et non le nom
			const result = await changePlan(selectedPlanForPayment.id, paymentType, paymentInfo);
			
			// Vérifier si c'est un paiement Mobile Money en attente
			if (result.pendingPayment && paymentType === 'mobile') {
				console.log('📱 Paiement Mobile Money en attente, démarrage du polling...');
				
				// Fermer le modal de paiement
				setShowPaymentModal(false);
				
				try {
					// Démarrer le polling et attendre la confirmation
					await startMobileMoneyPolling(result.pendingPayment);
					
					// Si on arrive ici, le paiement est réussi
					setSelectedPlanForPayment(null);
					setFormError('');
					setPromoCode('');
					setPromoDiscount(0);
					
					// Rediriger vers la page d'abonnement
					navigate('/abonnement?tab=confirm');
					
				} catch (mobileMoneyError) {
					// L'erreur est déjà gérée par le hook (toast affiché)
					console.error('❌ Paiement Mobile Money échoué:', mobileMoneyError);
					// Rester sur la page pour permettre de réessayer
				}
				return;
			}
			
			// Paiement direct réussi (carte bancaire)
			if (result.success) {
				// Fermer le modal et réinitialiser
				setShowPaymentModal(false);
				setSelectedPlanForPayment(null);
				setFormError('');
				setPromoCode('');
				setPromoDiscount(0);
				
				// Rediriger vers la page d'abonnement
				navigate('/abonnement?tab=confirm');
			} else {
				// Erreur de paiement
				setFormError(result.error || 'Une erreur est survenue');
			}
			
		} catch (error) {
			// En cas d'erreur, fermer le modal et revenir aux plans
			// Le toast d'erreur est déjà affiché par le hook useSubscription
			setShowPaymentModal(false);
			setSelectedPlanForPayment(null);
			setFormError('');
			setPromoCode('');
			setPromoDiscount(0);
		}
	};

	// Gestion de l'annulation du polling Mobile Money
	const handleMobileMoneyCancel = () => {
		cancelMobileMoneyPolling();
		resetMobileMoneyState();
	};

	// Gestion de la fermeture du status Mobile Money
	const handleMobileMoneyClose = () => {
		resetMobileMoneyState();
		if (mobileMoneyState.status === 'completed') {
			navigate('/abonnement?tab=confirm');
		}
	};

	// Gestion du retry Mobile Money
	const handleMobileMoneyRetry = () => {
		resetMobileMoneyState();
		setShowPaymentModal(true);
	};

	const handlePlanSelect = (plan: any) => {
		// Pour les plans personnalisés, toujours vers le formulaire de contact
		if (plan.isCustom) {
			window.open('mailto:ikiota@ikiotahub.com?subject=Demande de solution personnalisée', '_blank');
			return;
		}

		// Logique pour les utilisateurs non connectés
		if (!user) {
			// Ouvrir le modal de connexion/inscription
			setSelectedPlan(plan);
			setLoginModalOpen(true);
			return;
		}
		
		// Logique pour les utilisateurs connectés
		if (plan.id === 'sme-freemium' || plan.id === 'financial-freemium') {
			// Pour les plans gratuits, rediriger vers activation directe
			navigate(`/abonnement?plan=${plan.id}&activate=true`);
		} else {
			// Pour les plans payants, ouvrir le modal de paiement
			// ⚠️ Important: conserver l'UUID du plan pour l'API /subscriptions/purchase
			setSelectedPlanForPayment({
				id: plan.id,                    // 🔑 UUID requis pour le backend
				name: plan.name,
				description: plan.description,
				price: plan.price,
				features: plan.features,
				highlighted: plan.highlighted || false,
				demo: plan.demo || false,
				tokenAmount: plan.tokenAmount,
				configId: plan.configId,
				customerType: plan.customerType
			});
			setPaymentInfo((prev: PaymentDetails) => ({
				...prev,
				amount: plan.price
			}));
			setShowPaymentModal(true);
		}
	};

	const handleWhatsAppContact = () => {
		const whatsappUrl = 'https://wa.me/243972252499?text=Bonjour, je souhaite prendre rendez-vous pour discuter d\'une solution personnalisée Wanza.';
		window.open(whatsappUrl, '_blank');
	};

	const getPrice = (price: string) => {
		if (price === 'Sur devis') return price;
		const monthlyPrice = parseFloat(price);
		return isAnnual ? (monthlyPrice * 12 * 0.8).toFixed(1) : price; // 20% discount for annual
	};

	const getOriginalAnnualPrice = (price: string) => {
		if (price === 'Sur devis') return price;
		const monthlyPrice = parseFloat(price);
		return (monthlyPrice * 12).toFixed(1);
	};

	return (
		<section className="relative w-full py-16 bg-gradient-to-b from-gray-50 to-white">
			<Container>
				{/* En-tête moderne et épuré */}
				<div className="mx-auto max-w-4xl text-center mb-12">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5 }}
					>
						<span className="inline-block px-4 py-1.5 mb-4 text-sm font-semibold text-primary bg-primary/10 rounded-full">
							Tarification
						</span>
						<h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl mb-4">
							Choisissez votre solution
						</h1>
						<p className="text-lg text-gray-600 max-w-2xl mx-auto">
							Commencez gratuitement et évoluez selon vos besoins
						</p>
					</motion.div>
				</div>

				{/* Contrôles modernes en ligne avec onglets */}
				<div className="mx-auto max-w-4xl mb-12">
					<div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-2">
						<div className="flex flex-col sm:flex-row items-center justify-between gap-4">
							{/* Onglets : PME / Coopérative / Institution */}
							<div className="flex-1 w-full sm:w-auto">
								<div className="inline-flex w-full sm:w-auto rounded-xl bg-gray-100 p-1">
									<button
										onClick={() => {
											setSelectedCustomerType(CustomerType.SME);
											setIsCooperativeView(false);
										}}
										className={`flex-1 sm:flex-initial px-5 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
											selectedCustomerType === CustomerType.SME && !isCooperativeView
												? 'bg-white text-primary shadow-sm'
												: 'text-gray-600 hover:text-gray-900'
										}`}
									>
										PME
									</button>
									<button
										onClick={() => {
											setSelectedCustomerType(CustomerType.SME);
											setIsCooperativeView(true);
										}}
										className={`flex-1 sm:flex-initial px-5 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
											isCooperativeView
												? 'bg-white text-primary shadow-sm'
												: 'text-gray-600 hover:text-gray-900'
										}`}
									>
										Coopérative
									</button>
									<button
										onClick={() => {
											setSelectedCustomerType(CustomerType.FINANCIAL_INSTITUTION);
											setIsCooperativeView(false);
										}}
										className={`flex-1 sm:flex-initial px-5 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
											selectedCustomerType === CustomerType.FINANCIAL_INSTITUTION
												? 'bg-white text-primary shadow-sm'
												: 'text-gray-600 hover:text-gray-900'
										}`}
									>
										Institution
									</button>
								</div>
							</div>

							{/* Séparateur vertical */}
							<div className="hidden sm:block w-px h-8 bg-gray-200" />

							{/* Toggle Période de facturation */}
							<div className="flex-1 w-full sm:w-auto flex justify-end">
								<div className="inline-flex w-full sm:w-auto rounded-xl bg-gray-100 p-1">
									<button
										onClick={() => setIsAnnual(false)}
										className={`flex-1 sm:flex-initial px-4 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
											!isAnnual ? 'bg-white text-primary shadow-sm' : 'text-gray-600 hover:text-gray-900'
										}`}
									>
										Mensuel
									</button>
									<button
										onClick={() => setIsAnnual(true)}
										className={`flex-1 sm:flex-initial px-4 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 flex items-center gap-1 ${
											isAnnual ? 'bg-white text-primary shadow-sm' : 'text-gray-600 hover:text-gray-900'
										}`}
									>
										Annuel
										<span className="inline-block text-[10px] font-bold bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
											-20%
										</span>
									</button>
								</div>
							</div>
						</div>
					</div>

					{/* Carte de description avec animation */}
					<motion.div
						key={`${selectedCustomerType}-${isCooperativeView}`}
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.3 }}
						className="mt-6 bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-6 border border-primary/20"
					>
						<div className="flex items-start gap-4">
							<div className="flex-shrink-0">
								<div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
									{selectedCustomerType === CustomerType.SME ? (
										<Building2 className="w-6 h-6 text-primary" />
									) : (
										<Landmark className="w-6 h-6 text-primary" />
									)}
								</div>
							</div>
							<div className="flex-1 text-left">
								<h3 className="text-xl font-bold text-gray-900 mb-2">
									{isCooperativeView
										? 'Solution Coopérative — détaillants & artisans'
										: selectedCustomerType === CustomerType.SME
											? 'Solutions ERP pour PME'
											: 'Solutions de Gestion de Portefeuille'
									}
								</h3>
								<p className="text-gray-700">
									{isCooperativeView
										? 'Une plateforme partagée pour coopératives de détaillants et artisans : comptabilité par coopérant, stocks et ventes consolidés, accès au crédit collectif. Une solution face à la fragmentation du marché.'
										: selectedCustomerType === CustomerType.SME
											? 'ERP complet avec comptabilité, CRM, RH, gestion de stock et accès aux financements'
											: 'Prospection ciblée, gestion de portefeuille, centrale de risque et simulation de produits financiers'
									}
								</p>
							</div>
						</div>
					</motion.div>
				</div>

					{/* Vue coopérative — carte unique avec slider per-member */}
					{isCooperativeView && (
						<motion.div
							key="cooperative-card"
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.3 }}
							className="mx-auto mt-12 max-w-2xl"
						>
							<div className="relative flex flex-col rounded-2xl bg-white p-8 shadow-lg ring-2 ring-primary">
								<div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full shadow">
									Plan Coopérative
								</div>

								<div className="mb-6 text-center">
									<h3 className="text-2xl font-bold text-gray-900">
										Tarification par coopérant
									</h3>
									<p className="mt-2 text-sm text-gray-500">
										5 USD par coopérant et par mois. De 1 à 500 coopérants.
									</p>
								</div>

								{/* Prix total live */}
								<div className="text-center mb-8 py-6 bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl border border-primary/15">
									<div className="text-sm text-gray-500 mb-1">Total mensuel</div>
									<div className="flex items-baseline justify-center gap-2">
										<span className="text-5xl font-extrabold text-gray-900">
											{(cooperativeMembers * 5 * (isAnnual ? 10 : 1)).toLocaleString('fr-FR')}
										</span>
										<span className="text-lg font-semibold text-gray-600">USD</span>
									</div>
									<div className="text-xs text-gray-500 mt-2">
										{isAnnual
											? `Facturé annuellement (${(cooperativeMembers * 5 * 10).toLocaleString('fr-FR')} USD/an ≈ 2 mois offerts)`
											: `${cooperativeMembers} coopérants × 5 USD/mois`
										}
									</div>
								</div>

								{/* Slider coopérants */}
								<div className="mb-8">
									<div className="flex items-center justify-between mb-3">
										<label htmlFor="coop-members" className="text-sm font-semibold text-gray-700">
											Nombre de coopérants
										</label>
										<span className="inline-flex items-center gap-2 bg-primary/10 text-primary text-sm font-bold px-3 py-1 rounded-full">
											{cooperativeMembers}
										</span>
									</div>
									<input
										id="coop-members"
										type="range"
										min={1}
										max={500}
										step={1}
										value={cooperativeMembers}
										onChange={(e) => setCooperativeMembers(parseInt(e.target.value, 10))}
										className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
									/>
									<div className="flex justify-between text-xs text-gray-400 mt-2">
										<span>1</span>
										<span>250</span>
										<span>500</span>
									</div>
									{/* Saisie directe pour les valeurs précises */}
									<div className="mt-4 flex items-center gap-2">
										<label htmlFor="coop-members-input" className="text-xs text-gray-500">
											Ou saisir directement :
										</label>
										<input
											id="coop-members-input"
											type="number"
											min={1}
											max={500}
											value={cooperativeMembers}
											onChange={(e) => {
												const v = Math.max(1, Math.min(500, parseInt(e.target.value || '1', 10) || 1));
												setCooperativeMembers(v);
											}}
											className="w-24 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
										/>
										<span className="text-xs text-gray-500">coopérants</span>
									</div>
								</div>

								{/* Inclusions */}
								<ul className="space-y-2.5 mb-8 text-sm text-gray-700">
									<li className="flex items-start gap-2">
										<Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
										<span>Comptabilité SYSCOHADA mutualisée pour la coopérative et chaque coopérant</span>
									</li>
									<li className="flex items-start gap-2">
										<Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
										<span>POS, stocks et ventes consolidés au niveau coopérative + journal par coopérant</span>
									</li>
									<li className="flex items-start gap-2">
										<Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
										<span>Score de crédit collectif renforcé par les transactions de tous les coopérants</span>
									</li>
									<li className="flex items-start gap-2">
										<Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
										<span>Accès au financement jusqu'à 10 000 USD sur 12 mois</span>
									</li>
									<li className="flex items-start gap-2">
										<Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
										<span>Mobile Money intégré (M-PESA, Airtel Money, Orange Money)</span>
									</li>
								</ul>

								<button
									type="button"
									onClick={() => {
										// Persiste le nombre de coopérants pour la création de Subscription
										// (le backend lit `quantity` lors du POST /subscriptions).
										sessionStorage.setItem('cooperative_member_count', String(cooperativeMembers));
										navigate('/register?variant=cooperative');
									}}
									className="w-full bg-primary text-white font-semibold py-3 rounded-xl hover:bg-primary-hover transition-colors shadow"
								>
									Démarrer avec {cooperativeMembers} coopérant{cooperativeMembers > 1 ? 's' : ''}
								</button>

								<p className="text-xs text-gray-400 text-center mt-3">
									Vous pourrez ajuster le nombre de coopérants à tout moment.
								</p>
							</div>
						</motion.div>
					)}

					{/* Vue PME / Institution Financière — grille standard */}
					{!isCooperativeView && (
					<div className="mx-auto mt-12 grid max-w-6xl grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-2">
						{getDisplayPlans().map((plan) => {
							const isCurrent = Boolean(user && (plan.name.toLowerCase() === currentPlan.toLowerCase()));
							return (
								<motion.div
									key={plan.name}
									initial={{ opacity: 0, y: 20 }}
									whileInView={{ opacity: 1, y: 0 }}
									viewport={{ once: true }}
									className={`relative flex flex-col rounded-2xl bg-white p-8 shadow-sm ring-1 ${(plan as any).highlighted ? 'ring-primary' : (plan as any).isCustom ? 'ring-success' : 'ring-gray-200'}`}
								>
									{/* Badge plan actuel */}
									{isCurrent && (
										<span className="absolute top-4 right-4 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full z-20 shadow">Plan actuel</span>
									)}
									{(plan as any).highlighted && (
										<div className="absolute -top-4 -right-4 w-24 h-24 bg-primary rounded-full opacity-10 blur-2xl" />
									)}
									
									<div className="mb-6">
										<h3 className="text-lg font-semibold text-gray-900">
											{plan.name}
										</h3>
										<p className="mt-2 text-sm text-gray-500">
											{plan.description}
										</p>
										<p className="mt-6">
											{isAnnual && plan.price !== 'Sur devis' && (
												<span className="text-sm font-semibold text-red-500 line-through mr-2">
													${getOriginalAnnualPrice(plan.price)}
												</span>
											)}
											<span className="text-4xl font-bold tracking-tight text-gray-900">
												{plan.price === 'Sur devis' ? getPrice(plan.price) : `$${getPrice(plan.price)}`}
											</span>
											{plan.price !== 'Sur devis' && (
												<span className="text-sm font-semibold text-gray-600">
													{isAnnual ? '/an' : '/mois'}
												</span>
											)}
										</p>
									</div>

									<ul className="flex-1 space-y-4">
										{plan.features.map((feature) => {
											const isIncluded = feature.startsWith('✓');
											const isExcluded = feature.startsWith('✗');
											const featureText = feature.replace(/^[✓✗]\s*/, '');
											
											return (
												<li key={feature} className="flex items-start">
													<div className="flex-shrink-0">
														{isIncluded && <Check className="h-5 w-5 text-green-500" />}
														{isExcluded && <X className="h-5 w-5 text-red-400" />}
														{!isIncluded && !isExcluded && <Check className="h-5 w-5 text-primary" />}
													</div>
													<span className={`ml-3 text-sm ${isExcluded ? 'text-gray-400 line-through' : 'text-gray-600'}`}>
														{featureText}
													</span>
												</li>
											);
										})}
										
										{/* Affichage enrichi des tokens pour les plans payants */}
										{(plan as any).tokenAmount && (
											<li className="flex items-start border-t pt-4 mt-4">
												<div className="flex-shrink-0">
													<Zap className="h-5 w-5 text-primary" />
												</div>
												<div className="ml-3">
													<span className="text-sm text-gray-600">
														{(plan as any).tokenAmount} tokens inclus
													</span>
												</div>
											</li>
										)}
									</ul>

									<button
										onClick={() => handlePlanSelect(plan)}
										className={`mt-8 w-full rounded-full px-4 py-2.5 text-sm font-semibold text-white shadow-sm 
											${(plan as any).isCustom ? 'bg-success hover:bg-success-600' : 'bg-primary hover:bg-primary-hover'} 
											${isCurrent ? 'opacity-60 cursor-not-allowed' : ''}`}
										disabled={isCurrent}
										title={isCurrent ? 'C\'est votre plan actuel' : !user && !(plan as any).isCustom ? 'Connexion requise pour souscrire' : ''}
									>
										{isCurrent ? 'Votre plan actuel' : 
											(plan as any).isCustom ? 
												((plan as any).ctaText || 'Prendre rendez-vous') : 
												user ? 
													(plan.price === '0' ? 'Activer maintenant' : 'Souscrire') : 
													(plan.price === '0' ? 'Essayer gratuitement' : 'Se connecter pour souscrire')}
									</button>

									{/* Boutons de contact pour les plans personnalisés */}
									{(plan as any).isCustom && (
										<div className="mt-3 space-y-2">
											<button
												onClick={handleWhatsAppContact}
												className="w-full rounded-full px-4 py-2.5 text-sm font-semibold text-success border-2 border-success bg-white hover:bg-success hover:text-white transition-all duration-200 flex items-center justify-center gap-2"
											>
												<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
													<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
												</svg>
												WhatsApp
											</button>
											<button
												onClick={() => window.open('mailto:ikiota@ikiotahub.com?subject=Demande de solution personnalisée', '_blank')}
												className="w-full rounded-full px-4 py-2.5 text-sm font-semibold text-primary border-2 border-primary bg-white hover:bg-primary hover:text-white transition-all duration-200 flex items-center justify-center gap-2"
											>
												<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
													<path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
												</svg>
												Email
											</button>
										</div>
									)}
								</motion.div>
							);
						})}
					</div>
					)}
				</Container>
			
			{/* Modal de connexion/inscription */}
			{loginModalOpen && selectedPlan && (
				<LoginPromptModal
					isOpen={loginModalOpen}
					onClose={() => setLoginModalOpen(false)}
					planName={selectedPlan.name}
					planId={selectedPlan.id}
					customerType={selectedPlan.customerType}
				/>
			)}

			{/* Modal de paiement */}
			<PaymentModal
				plan={selectedPlanForPayment}
				isOpen={showPaymentModal}
				onClose={() => {
					setShowPaymentModal(false);
					setSelectedPlanForPayment(null);
					setFormError('');
					setPromoCode('');
					setPromoDiscount(0);
				}}
				onSubmit={handlePaymentSubmit}
				paymentType={paymentType}
				setPaymentType={setPaymentType}
				paymentInfo={paymentInfo}
				setPaymentInfo={setPaymentInfo}
				error={formError}
				billingPeriod={billingPeriod}
				setBillingPeriod={setBillingPeriod}
				promoCode={promoCode}
				setPromoCode={setPromoCode}
				promoDiscount={promoDiscount}
				onValidatePromo={validatePromoCode}
				onSetPromoDiscount={setPromoDiscount}
			/>

			{/* Modal de statut Mobile Money (polling) */}
			{mobileMoneyState.status !== 'idle' && (
				<MobileMoneyStatusDisplay
					state={mobileMoneyState}
					onCancel={handleMobileMoneyCancel}
					onRetry={handleMobileMoneyRetry}
					onClose={handleMobileMoneyClose}
				/>
			)}
		</section>
	);
}
